import { encodeId } from './idCodec';

/**
 * Builds public storefront URLs from inside the panel.
 *
 * Extracted from `ProductQrCard`, which already resolved the storefront origin
 * this way for printed QR codes. Sharing a product needs the identical URL — a
 * scanned label and a shared link must land on the same page — so the logic
 * lives in one place rather than being reimplemented per feature.
 *
 * Origin resolution order:
 *   1. `window.__KSS_CONFIG__.storefrontOrigin`  ← runtime-config override
 *   2. `window.location.origin`                   ← same-origin fallback
 *
 * The override is REQUIRED when panel and storefront are on different origins:
 * local dev (different ports) and split-domain production. Without it a shared
 * link carries the panel's origin, which has no `/shop/:id` route — the
 * recipient lands on a 404 or the admin login screen.
 */
export function storefrontOrigin(): string {
  if (typeof window === 'undefined') return '';
  const configured = (window as unknown as {
    __KSS_CONFIG__?: { storefrontOrigin?: string };
  }).__KSS_CONFIG__?.storefrontOrigin;
  const base = configured?.trim() || window.location?.origin || '';
  // Trailing slash trimmed so callers can safely template `${origin}/shop/...`.
  return base.replace(/\/+$/, '');
}

/** Public product page. Ids are base64url-obfuscated, matching `/shop/:id`. */
export function productUrl(productId: string): string {
  return `${storefrontOrigin()}/shop/${encodeId(productId)}`;
}

/**
 * Product page that adds the item to the cart and goes straight to checkout.
 *
 * This is the "payment link": there is no gateway-issued pay-by-link today
 * (`StubPaymentGateway` is the only implementation), so the closest real thing
 * is dropping the recipient into the existing checkout with the item already in
 * their basket. They then pay through the normal COD / UPI / card flow.
 *
 * `qty` is clamped to a sane range — the value ends up in a URL anyone can edit,
 * and an absurd quantity should not reach the cart.
 */
export function productBuyNowUrl(productId: string, qty = 1): string {
  const n = Math.min(Math.max(Math.floor(Number(qty) || 1), 1), 999);
  return `${productUrl(productId)}?buy=1&qty=${n}`;
}

/** Public order-tracking page for a sales order. */
export function orderUrl(salesOrderId: string): string {
  return `${storefrontOrigin()}/account/orders/${encodeId(salesOrderId)}`;
}

/** Customer-facing invoice page. */
export function invoiceUrl(invoiceId: string): string {
  return `${storefrontOrigin()}/account/invoices/${encodeId(invoiceId)}`;
}

/**
 * True when a URL points somewhere a recipient's phone cannot reach.
 *
 * Sharing a localhost link is a silent failure — it looks fine to the sender and
 * is dead on arrival for everyone else. Callers surface this as a warning rather
 * than blocking the share, since it is legitimate while testing locally.
 */
export function isUnreachableHost(url: string): boolean {
  try {
    const host = new URL(url).hostname;
    return host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local');
  } catch {
    return false;
  }
}
